import { useMemo, useState } from "preact/hooks";

import { ContactFormSheet } from "../components/ContactFormSheet";
import type { useContacts } from "../hooks/useContacts";
import {
  ADD_CONTACT_BUTTON_LABEL,
  CONTACT_ARCHIVED_TAG_LABEL,
  CONTACTS_EMPTY_HINT,
  CONTACTS_FILTER_ACTIVE_LABEL,
  CONTACTS_FILTER_ALL_LABEL,
  CONTACTS_FILTER_TOGGLE_LABEL,
  CONTACTS_SHOW_ALL_LABEL,
  contactArchivedMessage,
  contactRestoredMessage,
  contactsListCountAnnouncement,
  noActiveContactsHint,
} from "../lib/recordsCopy";
import type { ContactOut } from "../types";
import { ContactDetailOverlay } from "./ContactDetailOverlay";

export interface ContactsViewProps {
  contacts: ReturnType<typeof useContacts>;
  isAdmin: boolean;
  onNotify: (message: string) => void;
}

type ContactsFilter = "active" | "all";

/**
 * Sprint-010 C4 — the routed `/contacts` screen's list: name-ascending,
 * `.admin-list`/`.admin-row`, tap through to detail. Reached by admin/
 * manager — a member never sees this screen at all (`ContactsScreen`'s
 * `canHandOutWork` gate, matching `GET /contacts`'s own 403 for that role —
 * ADR-002 §5/§11).
 *
 * Sprint-013 C3 (design-spec-animal-cards-2026-07-10.md §5, ux-ruling-
 * inactive-grid-2026-07-10.md — E1 PM ruling, mirrored here per the task's
 * own binding instruction; sa-ruling-crud-endpoints-2026-07-10.md Decision
 * 3) adds the header "+ Add" entry point, an "Active" (default) / "All"
 * toggle, and admin-only Add/Edit/Archive/Restore — mirroring
 * `ProjectsView` field-for-field. Rows become tappable `<button>`s for the
 * first time this sprint (previously plain, non-interactive `<div>`s —
 * `contacts-screen.test.tsx`'s old "never a `<button>`" assertion predates
 * this detail surface and is updated alongside this change). No toggle
 * state is persisted across mounts — every fresh visit starts on "Active,"
 * same as `AnimalsView`/`ProjectsView`.
 */
export function ContactsView({ contacts, isAdmin, onNotify }: ContactsViewProps) {
  const [openId, setOpenId] = useState<string | null>(null);
  const [filter, setFilter] = useState<ContactsFilter>("active");
  const [showAdd, setShowAdd] = useState(false);
  const openContact = contacts.contacts?.find((c) => c.id === openId) ?? null;

  const allContacts = contacts.contacts ?? [];
  const activeContacts = useMemo(
    () => allContacts.filter((c) => c.archived_at == null),
    [allContacts],
  );
  const archivedCount = allContacts.length - activeContacts.length;
  const visibleContacts = filter === "all" ? allContacts : activeContacts;

  return (
    <div>
      <div class="animals-view-toolbar">
        <div class="animals-filter-toggle" role="group" aria-label={CONTACTS_FILTER_TOGGLE_LABEL}>
          <button
            type="button"
            class={`animals-filter-btn${filter === "active" ? " is-selected" : ""}`}
            aria-pressed={filter === "active"}
            onClick={() => setFilter("active")}
          >
            {CONTACTS_FILTER_ACTIVE_LABEL}
          </button>
          <button
            type="button"
            class={`animals-filter-btn${filter === "all" ? " is-selected" : ""}`}
            aria-pressed={filter === "all"}
            onClick={() => setFilter("all")}
          >
            {CONTACTS_FILTER_ALL_LABEL}
          </button>
        </div>
        {isAdmin ? (
          <button
            type="button"
            class="primary-btn animals-add-btn"
            onClick={() => setShowAdd(true)}
          >
            {ADD_CONTACT_BUTTON_LABEL}
          </button>
        ) : null}
      </div>

      {/* Mirrors `ProjectsView`/`AnimalsView`'s own live-region convention —
       * the announced count refreshes when the toggle changes. */}
      <p class="sr-only" aria-live="polite">
        {contacts.loading || contacts.error
          ? ""
          : contactsListCountAnnouncement(visibleContacts.length)}
      </p>

      {contacts.loading ? (
        <p class="loading-hint">Loading contacts…</p>
      ) : contacts.error ? (
        <p class="error-hint" role="alert">
          {contacts.error}
        </p>
      ) : allContacts.length === 0 ? (
        <p class="empty-hint">{CONTACTS_EMPTY_HINT}</p>
      ) : visibleContacts.length === 0 ? (
        <p class="empty-hint">
          {noActiveContactsHint(archivedCount)}{" "}
          <button type="button" class="inline-link-btn" onClick={() => setFilter("all")}>
            {CONTACTS_SHOW_ALL_LABEL}
          </button>
        </p>
      ) : (
        <ul class="admin-list">
          {visibleContacts.map((contact) => (
            <li key={contact.id}>
              <ContactRow contact={contact} onOpen={() => setOpenId(contact.id)} />
            </li>
          ))}
        </ul>
      )}

      {isAdmin ? (
        <ContactFormSheet
          open={showAdd}
          mode="add"
          onCancel={() => setShowAdd(false)}
          onAdd={async (fields) => {
            const created = await contacts.createContact(fields);
            setShowAdd(false);
            onNotify(`Added ${created.name}.`);
          }}
        />
      ) : null}

      {openContact ? (
        <ContactDetailOverlay
          contact={openContact}
          isAdmin={isAdmin}
          onClose={() => setOpenId(null)}
          onNotify={onNotify}
          onSaveContact={async (contactId, body) => {
            await contacts.patchContact(contactId, body);
          }}
          onArchive={async () => {
            await contacts.archiveContact(openContact.id);
            setOpenId(null);
            onNotify(contactArchivedMessage(openContact.name));
          }}
          onRestore={async () => {
            await contacts.restoreContact(openContact.id);
            setOpenId(null);
            onNotify(contactRestoredMessage(openContact.name));
          }}
        />
      ) : null}
    </div>
  );
}

function ContactRow({ contact, onOpen }: { contact: ContactOut; onOpen: () => void }) {
  const isArchived = contact.archived_at != null;
  return (
    <button type="button" class={`admin-row${isArchived ? " is-archived" : ""}`} onClick={onOpen}>
      <span class="admin-row-main">
        <span class="admin-row-name">
          {contact.name}
          {isArchived ? (
            <span class="admin-tag is-archived">{CONTACT_ARCHIVED_TAG_LABEL}</span>
          ) : null}
        </span>
        {contact.contact ? <span class="admin-row-sub">{contact.contact}</span> : null}
        {contact.notes ? <span class="admin-row-sub">{contact.notes}</span> : null}
      </span>
      <span class="chevron" aria-hidden="true">
        ›
      </span>
    </button>
  );
}
